import {TimelineLite, TweenLite} from "gsap";
import {
    SimplePresentationRunner,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {PearlHarbor} from "../../PearlHarbor";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {PearlHarborAvalancheReelModel} from "../../reels/PearlHarborAvalancheReelModel";
import {ISymbol} from "../../../core/reel/IReelData";
import {ISplitData} from "../../server/PearlHarborModel";
import {PearlHarborGameConfig} from "../../PearlHarborGameConfig";
import {PearlHarborSplitSymbol} from "../../reels/PearlHarborSplitSymbol";
import {PearlHarborSymbol} from "../../reels/PearlHarborSymbol";
import {PearlHarborSoundManager} from "../../sound/PearlHarborSoundManager";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";

export class PearlHarborXSWinRunner implements SimplePresentationRunner {
    private SPLIT_DIR: string[] = ["up", "down", "left", "right"];
    private readonly ANIMATION_NAME: string[] = [
        "add_sword_up", "add_sword_down", "add_sword_left", "add_sword_right",
        "land_up", "land_down", "land_left", "land_right",
        "split_up", "split_down", "split_left", "split_right",
        "still_up", "still_down", "still_left", "still_right",
        "idle_petals", "single", "turn_wild", "unlock"
    ];
    public readonly animationConfig = {"add_sword": 19, "landing": 13, "split": 30, "turningWild": 22};

    private _mainTL: TimelineMax;
    private _controller: SimplePresentationRunnerController;
    private _affectedReels: boolean[] = [];

    private complete(): void {
        this._controller.finish();
    }

    shouldRun(): boolean {
        return PearlHarbor.model.xsThisTurn.length > 0;
    }

    private createSplitTl(symbol: ISymbol, animationName: string, scale: number[]): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            layer: StageLayer = NolimitApplication.getLayerByName("XSplitAnimation")!,
            spineObj: PIXI.spine.Spine = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset("sword_sweep"));
        spineObj.scale.set(scale[0], scale[1]);
        layer.addChild(spineObj);
        spineObj.position.set((symbol.reelId) * PearlHarborGameConfig.symbolWidth, (symbol.reelTargetPosition + 1) * PearlHarborGameConfig.symbolHeight * 0.5-((<PearlHarborSymbol>symbol).isSplit?24:0));
        tl.add(new SpineTween(spineObj, 0, animationName));
        tl.add(() => {
            NolimitApplication.getLayerByName("XSplitAnimation")!.removeChild(spineObj);
            if(spineObj){
                spineObj.destroy();
            }
        });
        return tl;
    }

    public addAffectedReelsOnBigSymbol(orgSym: PearlHarborSymbol, affectedReels: boolean[]): boolean[] {
        let reelSize: number = orgSym.symSize[0],
            startReelIndex: number = Math.floor(orgSym.topLeftSeed / 14);
        if (startReelIndex < 0) {
            debugger;
        }

        for (let i: number = 0; i < reelSize; i++) {
            affectedReels[i + startReelIndex] = true;
        }
        return affectedReels;
    }

    private getEachSplitAnimation(reelId: number, onReelTargetPosition: number, dir: boolean[]): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            splitSymbol: ISymbol = PearlHarbor.allReelsView.mainReelModels[reelId].getSymbolByTargetPosition(onReelTargetPosition)!;

        let onReelId: number = splitSymbol.onReelId;
        let affectedReels: boolean[] = ArrayHelper.initArrayWithValues(7, (i: number) => this._affectedReels[i]);
        tl.add((<PearlHarborSplitSymbol>splitSymbol).playSplitAnim(), 0);

        tl.add([(<PearlHarborSplitSymbol>splitSymbol).playToWildAnim()], 0.6);
        let delay: number = tl.duration() - 0.3;
        const reel: PearlHarborAvalancheReelModel = PearlHarbor.allReelsView.mainReelModels[reelId],
            splitUp: boolean = dir[0] && onReelId > 0,
            splitDown: boolean = dir[1] && onReelId < (PearlHarbor.model.reels[reelId].length - 1),
            splitLeft: boolean = dir[2] && reelId > 0,
            splitRight: boolean = dir[3] && reelId < 6,
            splitAnimations: TimelineLite[] = [];
        if (splitUp) {
            splitAnimations.push(this.createSplitTl(splitSymbol, `v_${Math.min(onReelId, 6)}`, [1, 1]));
        }
        if (splitDown) {
            splitAnimations.push(this.createSplitTl(splitSymbol, `v_${Math.min(PearlHarbor.model.reels[reelId].length - onReelId - 1, 6)}`, [1, -1]));
        }
        if (splitLeft) {
            splitAnimations.push(this.createSplitTl(splitSymbol, `h_${Math.max(1, reelId)}`, [1, 1]));
        }
        if (splitRight) {
            splitAnimations.push(this.createSplitTl(splitSymbol, `h_${Math.max(1, 6 - reelId)}`, [-1, 1]));
        }
        tl.add(splitAnimations, delay);

        if (splitUp || splitDown) {
            onReelId = splitSymbol.onReelId;
            const allOutSymbols: ISymbol[] = reel.getAllSymbols(),
                top: number = splitUp ? 0 : (onReelId + 1),
                bottom: number = splitDown ? allOutSymbols.length : onReelId;
            affectedReels[reelId] = true;
            for (let i: number = top; i < bottom; i++) {
                const symbol: ISymbol = allOutSymbols[i];
                const phSymbol: PearlHarborSymbol = (<PearlHarborSymbol>symbol);
                if (phSymbol.isBig) {
                    affectedReels = this.addAffectedReelsOnBigSymbol(phSymbol, affectedReels);
                }
            }
        }

        if (splitLeft || splitRight) {
            const left: number = splitLeft ? 0 : (reelId + 1),
                right: number = splitRight ? 7 : reelId;
            for (let s: number = left; s < right; s++) {
                affectedReels[s] = true;
            }
        }
        this._affectedReels = affectedReels;

        return tl;
    }

    start(controller: SimplePresentationRunnerController): void {
        let startFrame: number = this.animationConfig.landing,fs: number = 0.033;
        this._controller = controller;
        this._mainTL = new TimelineLite();
        this._mainTL.add(()=>{
            PearlHarborSoundManager.playXSToWildSound()
        },startFrame*fs);
        for (let n: number = 0; n < PearlHarbor.model.xsThisTurn.length; n++) {
            const xSplits: ISplitData = PearlHarbor.model.xsThisTurn[n],
                symPos: number[] = xSplits.symPos;
            this._mainTL.add(this.getEachSplitAnimation(symPos[0], symPos[1], xSplits.dir), startFrame*fs);
        }

        this._affectedReels.forEach((affected: boolean, i: number) => {
            if (affected) {
                PearlHarbor.allReelsView.mainReelModels[i].reelView.replaceSplitSymbols();
            }
        });

        this._mainTL.add(() => {
            this._affectedReels.forEach((affected: boolean, i: number) => {
                if (affected) {
                    PearlHarbor.allReelsView.mainReelModels[i].reelView.showSplitSymbols();
                }
            });
        }, 1.4);
        this._mainTL.add(() => {}, 1.5);
        if (this._mainTL.duration() == 0) {
            this._mainTL.add(TweenLite.to(this, {duration: 0.001}));
        }
        this._mainTL.add(() => this.complete());
    }
}